import React from 'react';

import Icon from 'views/site/Icon.js';

import Button from 'views/site/Button.js';
import Theme from 'styles/Theme.js';
import Utils from 'files/Utils.js';

/*
 * QuoteDrawer
 *
 * The storefront's cart drawer with the money taken out. What is left is a
 * list of what the visitor wants quoted, the quantities, and a way to send it.
 *
 * The footer is where the difference shows. A cart footer's job is to remove
 * doubt about a total before checkout; this one's job is the opposite — to set
 * the expectation that a person will follow up, and that nothing has been
 * committed to.
 */

const QuoteDrawer = ({ items, onClose, onRemoveItem, onRequestQuote, onUpdateItem, open }) => {

    const list = items || [];

    const getItem = (item, index) => {

        let last = index === list.length - 1;

        const step = (icon, onClick, label) => (
            <button
            aria-label={label}
            onClick={onClick}
            style={{
                alignItems: 'center',
                backgroundColor: 'transparent',
                color: Theme.colors.darkGrey,
                display: 'flex',
                fontSize: 12,
                height: 32,
                justifyContent: 'center',
                width: 32
            }}>
                <Icon name={icon} />
            </button>
        );

        return (
            <div
            key={item.handle}
            style={{
                borderBottom: last ? 'none' : `1px solid ${Theme.colors.border()}`,
                display: 'flex',
                gap: 16,
                marginBottom: last ? 0 : 20,
                paddingBottom: last ? 0 : 20
            }}>
                <div style={{
                    alignItems: 'center',
                    backgroundColor: Theme.colors.offWhite,
                    borderRadius: 12,
                    display: 'flex',
                    flexShrink: 0,
                    height: 80,
                    justifyContent: 'center',
                    overflow: 'hidden',
                    width: 80
                }}>
                    {item.images?.length > 0 ? (
                        <img
                        alt={item.title}
                        src={item.images[0].src}
                        style={{ height: '100%', objectFit: 'cover', width: '100%' }} />
                    ) : (
                        <Icon
                        color={Theme.colors.lighterGrey}
                        name={item.icon || 'box-seam'}
                        size={24} />
                    )}
                </div>

                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                    justifyContent: 'space-between',
                    minWidth: 0
                }}>
                    <div>
                        <span style={{
                            ...Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 14, fontWeight: 600 }),
                            display: 'block',
                            marginBottom: 4
                        }}>{item.title}</span>
                        <span style={Theme.textStyles.caption()}>{item.subtitle || item.category}</span>
                    </div>

                    <div style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: 8
                    }}>
                        <div style={{
                            alignItems: 'center',
                            border: `1px solid ${Theme.colors.border()}`,
                            borderRadius: 8,
                            display: 'flex',
                            overflow: 'hidden'
                        }}>
                            {step('dash-lg', onUpdateItem.bind(this, item.handle, item.quantity - 1), `Reduce ${item.title}`)}
                            <span style={{
                                ...Theme.textStyles.body({ color: Theme.colors.text(), fontSize: 13, fontWeight: 600 }),
                                textAlign: 'center',
                                width: 32
                            }}>{item.quantity}</span>
                            {step('plus-lg', onUpdateItem.bind(this, item.handle, item.quantity + 1), `Add another ${item.title}`)}
                        </div>

                        <button
                        aria-label={`Remove ${item.title}`}
                        onClick={onRemoveItem.bind(this, item.handle)}
                        style={{
                            backgroundColor: 'transparent',
                            color: Theme.colors.lightGrey,
                            fontSize: 13,
                            padding: '4px 8px'
                        }}>
                            <Icon name={'trash'} />
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <>
            <div
            className={`site-overlay ${open ? 'active' : ''}`}
            onClick={onClose} />

            <div
            className={'site'}
            style={{
                backgroundColor: Theme.colors.white,
                boxShadow: open ? '-4px 0 30px rgba(0,0,0,0.1)' : 'none',
                display: 'flex',
                flexDirection: 'column',
                height: '100vh',
                position: 'fixed',
                right: 0,
                top: 0,
                transform: open ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.35s ease',
                width: Utils.isMobile() ? '100%' : 420,
                zIndex: 6000
            }}>
                <div style={{
                    alignItems: 'center',
                    borderBottom: `1px solid ${Theme.colors.border()}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '20px 24px'
                }}>
                    <span style={Theme.textStyles.subHeading()}>{'Your Network'}</span>
                    <div
                    onClick={onClose}
                    style={{
                        alignItems: 'center',
                        backgroundColor: Theme.colors.offWhite,
                        borderRadius: 18,
                        cursor: 'pointer',
                        display: 'flex',
                        height: 36,
                        justifyContent: 'center',
                        width: 36
                    }}>
                        <Icon name={'x-lg'} size={14} color={Theme.colors.darkGrey} />
                    </div>
                </div>

                <div style={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    padding: 24
                }}>
                    {list.length === 0 ? (
                        <div style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 16,
                            height: '100%',
                            justifyContent: 'center'
                        }}>
                            <Icon name={'clipboard-check'} size={48} color={Theme.colors.lighterGrey} />
                            <span style={Theme.textStyles.body({ textAlign: 'center' })}>
                                {'Nothing in your network yet'}
                            </span>
                        </div>
                    ) : list.map(getItem)}
                </div>

                {list.length > 0 && (
                    <div style={{
                        backgroundColor: Theme.colors.white,
                        borderTop: `1px solid ${Theme.colors.border()}`,
                        padding: 24
                    }}>
                        <span style={{
                            ...Theme.textStyles.caption(),
                            display: 'block',
                            marginBottom: 16,
                            textAlign: 'center'
                        }}>{'Nothing is ordered or charged. Your local dealer walks your home with you first before any purchase is made.'}</span>

                        <Button
                        icon={'send-fill'}
                        label={'Request My In-Home Visit'}
                        onClick={onRequestQuote}
                        style={{ width: '100%' }}
                        type={'primary'} />
                    </div>
                )}
            </div>
        </>
    )
}

export default QuoteDrawer;
